import { Helmet } from "react-helmet";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Link } from "react-router-dom";
import { Star, Quote, CheckCircle, Shield, Users } from "lucide-react";

const AvisTransportColisFranceMaroc = () => {
  const testimonials = [
    {
      name: "Fatima B.",
      location: "Paris → Casablanca",
      rating: 5,
      text: "Première expérience avec GoBledPro et je suis très satisfaite. Le transporteur était ponctuel, le colis est arrivé en parfait état. Je recommande vivement.",
      date: "Décembre 2024"
    },
    {
      name: "Karim M.",
      location: "Lyon → Marrakech",
      rating: 5,
      text: "J'ai envoyé plusieurs valises pour les fêtes. Communication claire, prix correct, et livraison dans les temps. Rien à redire.",
      date: "Décembre 2024"
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>Avis Transport Colis France Maroc – Témoignages Clients | GoBledPro</title>
        <meta 
          name="description" 
          content="Découvrez les avis de nos clients sur le transport de colis entre la France et le Maroc. Témoignages authentiques et retours d'expérience sur GoBledPro." 
        />
        <link rel="canonical" href="https://gobledpro.com/avis-transport-colis-france-maroc" />
      </Helmet>

      <Header />

      <main className="flex-1 pt-24 pb-16">
        <div className="container mx-auto px-4">
          <article className="max-w-4xl mx-auto">
            {/* Hero Section */}
            <header className="text-center mb-12">
              <span className="inline-block text-sm font-medium text-primary bg-primary/10 px-3 py-1 rounded-full mb-4">
                Confiance
              </span>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-6">
                Avis sur le Transport de Colis France – Maroc
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
                Ce que nos utilisateurs disent de leur expérience avec les transporteurs référencés sur GoBledPro.
              </p>
            </header>

            {/* Introduction - Objectif de lancement */}
            <section className="prose prose-lg max-w-none mb-8">
              <h2 className="text-xl font-heading font-semibold text-foreground mb-3">Notre objectif de lancement</h2>
              <p className="text-muted-foreground leading-relaxed">
                GoBledPro se lance avec une ambition claire : offrir une expérience fiable et encadrée 
                entre clients et transporteurs référencés.
              </p>
            </section>

            {/* Stats - Présentés comme objectifs */}
            <section className="mb-12">
              <div className="grid grid-cols-3 gap-4 md:gap-6">
                <div className="bg-card border border-border rounded-xl p-4 md:p-6 text-center">
                  <div className="text-2xl md:text-3xl font-bold text-primary mb-1">4,7 / 5</div>
                  <div className="flex justify-center gap-0.5 mb-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star 
                        key={star} 
                        className={`w-4 h-4 ${star <= 4 ? 'text-yellow-400 fill-yellow-400' : 'text-yellow-400'}`} 
                      />
                    ))}
                  </div>
                  <p className="text-xs md:text-sm text-muted-foreground">Note moyenne visée</p>
                </div>
                <div className="bg-card border border-border rounded-xl p-4 md:p-6 text-center">
                  <div className="text-2xl md:text-3xl font-bold text-primary mb-2">500+</div>
                  <p className="text-xs md:text-sm text-muted-foreground">Envois visés lors des premières phases</p>
                </div>
                <div className="bg-card border border-border rounded-xl p-4 md:p-6 text-center">
                  <div className="text-2xl md:text-3xl font-bold text-primary mb-2">95 %</div>
                  <p className="text-xs md:text-sm text-muted-foreground">Objectif de satisfaction client</p>
                </div>
              </div>
            </section>

            {/* Témoignages */}
            <section className="mb-12">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-heading font-bold text-foreground">
                  Premiers retours utilisateurs
                </h2>
                <span className="text-xs text-muted-foreground bg-muted px-3 py-1 rounded-full">
                  Décembre 2024 (lancement)
                </span>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                {testimonials.map((testimonial, index) => (
                  <div key={index} className="bg-card border border-border rounded-xl p-6">
                    <div className="flex items-start gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Quote className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground">{testimonial.name}</h3>
                        <p className="text-sm text-muted-foreground">{testimonial.location}</p>
                      </div>
                    </div>
                    <div className="flex gap-0.5 mb-3">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star 
                          key={star} 
                          className={`w-4 h-4 ${star <= testimonial.rating ? 'text-yellow-400 fill-yellow-400' : 'text-muted-foreground'}`} 
                        />
                      ))}
                    </div>
                    <p className="text-muted-foreground text-sm mb-3">{testimonial.text}</p>
                    <p className="text-xs text-muted-foreground/70">{testimonial.date}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Comment nous collectons les avis */}
            <section className="mb-12">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-6">
                Comment nous collectons les avis
              </h2>
              <div className="bg-muted/30 rounded-2xl p-6 md:p-8">
                <p className="text-muted-foreground mb-6">
                  Nous tenons à la transparence et à l'authenticité des témoignages publiés :
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-foreground">Avis vérifiés</h4>
                      <p className="text-muted-foreground text-sm mt-1">
                        Seuls les utilisateurs ayant effectivement réalisé un envoi via la plateforme peuvent laisser un avis.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-foreground">Collecte post-livraison</h4>
                      <p className="text-muted-foreground text-sm mt-1">
                        Nous sollicitons les retours après confirmation de la réception du colis par le destinataire.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-foreground">Pas de censure</h4>
                      <p className="text-muted-foreground text-sm mt-1">
                        Les avis négatifs sont publiés au même titre que les positifs. Nous croyons en la transparence.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-foreground">Modération des abus</h4>
                      <p className="text-muted-foreground text-sm mt-1">
                        Seuls les contenus injurieux, hors-sujet ou manifestement frauduleux sont modérés.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Ce que les clients apprécient */}
            <section className="mb-12">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-6">
                Ce que nos clients apprécient
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-card border border-border rounded-xl p-6">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <Shield className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">Transporteurs vérifiés</h3>
                  <p className="text-muted-foreground text-sm">
                    La vérification des transporteurs rassure et donne confiance pour confier ses colis.
                  </p>
                </div>
                <div className="bg-card border border-border rounded-xl p-6">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <Users className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">Communication claire</h3>
                  <p className="text-muted-foreground text-sm">
                    Les transporteurs sont réactifs et communiquent régulièrement sur l'avancement.
                  </p>
                </div>
                <div className="bg-card border border-border rounded-xl p-6">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <Star className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">Prix justes</h3>
                  <p className="text-muted-foreground text-sm">
                    Des tarifs compétitifs et transparents, sans mauvaise surprise à la livraison.
                  </p>
                </div>
              </div>
            </section>

            {/* Points d'amélioration */}
            <section className="mb-12">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-6">
                Ce que nous améliorons
              </h2>
              <div className="bg-card border border-border rounded-2xl p-6 md:p-8">
                <p className="text-muted-foreground mb-6">
                  Nous écoutons les retours pour améliorer continuellement notre service. 
                  Voici les points sur lesquels nous travaillons :
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3 text-muted-foreground">
                    <span className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0"></span>
                    <span>Amélioration du suivi en temps réel des colis</span>
                  </li>
                  <li className="flex items-start gap-3 text-muted-foreground">
                    <span className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0"></span>
                    <span>Développement d'une application mobile</span>
                  </li>
                  <li className="flex items-start gap-3 text-muted-foreground">
                    <span className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0"></span>
                    <span>Extension du réseau de transporteurs dans certaines régions</span>
                  </li>
                  <li className="flex items-start gap-3 text-muted-foreground">
                    <span className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0"></span>
                    <span>Mise en place d'un système de notation plus détaillé</span>
                  </li>
                </ul>
              </div>
            </section>

            {/* CTA */}
            <section className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl p-8 md:p-12 text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-4">
                Rejoignez nos clients satisfaits
              </h2>
              <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                Faites confiance à des transporteurs vérifiés et bénéficiez d'un service de qualité 
                pour vos envois France-Maroc.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Link 
                  to="/demande-transport" 
                  className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-lg font-medium hover:bg-primary/90 transition-colors"
                >
                  Demander un transport
                </Link>
                <Link 
                  to="/transporteurs-verifies-maroc" 
                  className="text-primary hover:underline font-medium flex items-center gap-1"
                >
                  Nos transporteurs vérifiés →
                </Link>
              </div>
            </section>

            {/* Retour */}
            <div className="pt-8 border-t border-border">
              <Link 
                to="/" 
                className="text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                ← Retour à l'accueil
              </Link>
            </div>
          </article>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AvisTransportColisFranceMaroc;
